import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Chart as ChartJS, ArcElement, Title, Tooltip, Legend } from "chart.js";
import { Pie } from "react-chartjs-2";
import CloseIcon from "@mui/icons-material/Close";
import ListAltIcon from "@mui/icons-material/ListAlt";
import StorefrontIcon from "@mui/icons-material/Storefront";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AllInclusiveIcon from "@mui/icons-material/AllInclusive";
import { adminApiService } from "../../../utils/api";
import { ShoppingListFlyerItemChartResponseDto } from "@swagger/typescript-fetch-goflyer";
import dayjs, { Dayjs } from "dayjs";
import { toastError } from "../../../utils/pageUtil/toast";

ChartJS.register(ArcElement, Title, Tooltip, Legend);

interface ShoppingListChartModalProps {
  open: boolean;
  onClose: () => void;
}

const ORANGE = "#f58220";
const BLUE = "#36a2eb";

const datePickerSx = {
  "& .MuiOutlinedInput-root": {
    "&.Mui-focused fieldset": { borderColor: ORANGE },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: ORANGE },
};

const datePickerDaySx = {
  "&.Mui-selected": {
    backgroundColor: `${ORANGE} !important`,
    "&:hover": { backgroundColor: "#d97320 !important" },
  },
};

export const ShoppingListChartModal: React.FC<ShoppingListChartModalProps> = ({
  open,
  onClose,
}) => {
  const [chartData, setChartData] =
    useState<ShoppingListFlyerItemChartResponseDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState<Dayjs | null>(
    dayjs().subtract(30, "day"),
  );
  const [toDate, setToDate] = useState<Dayjs | null>(dayjs());

  const fetchChartData = async () => {
    setLoading(true);
    try {
      const response =
        await adminApiService.gfShoppingListFlyerItemAdminControllerGetAddedFromChart(
          fromDate?.format("YYYY-MM-DD"),
          toDate?.format("YYYY-MM-DD"),
          undefined,
        );
      setChartData(response);
    } catch (error) {
      console.error("Error fetching chart data:", error);
      toastError("Failed to load chart data");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open) fetchChartData();
  }, [open, fromDate, toDate]);

  const summary = chartData?.summary as any;
  const timeline = chartData?.timeline as any[];

  const pieData = summary
    ? {
        labels: ["Deals Page", "Smart Flyer"],
        datasets: [
          {
            label: "Items Added From",
            data: [summary.deals_page, summary.smart_flyer],
            backgroundColor: ["rgba(245,130,32,0.85)", "rgba(54,162,235,0.85)"],
            borderColor: [ORANGE, BLUE],
            borderWidth: 2,
          },
        ],
      }
    : null;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        {/* Header */}
        <DialogTitle
          sx={{
            backgroundColor: ORANGE,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 1.5,
            px: 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ListAltIcon />
            <Typography variant="h6" fontWeight={700}>
              Shopping List — Added From Analysis
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "#fff" }} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 2, backgroundColor: "#fafafa" }}>
          {/* Date Filter */}
          <Paper
            elevation={0}
            sx={{ p: 1.5, mb: 2, border: "1px solid #eee", borderRadius: 2 }}
          >
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} sm={4}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Filter by Date Range
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <DatePicker
                  label="From Date"
                  value={fromDate}
                  onChange={(v) => setFromDate(v)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      sx: datePickerSx,
                    },
                    day: { sx: datePickerDaySx },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <DatePicker
                  label="To Date"
                  value={toDate}
                  onChange={(v) => setToDate(v)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      sx: datePickerSx,
                    },
                    day: { sx: datePickerDaySx },
                  }}
                />
              </Grid>
            </Grid>
          </Paper>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress sx={{ color: ORANGE }} />
            </Box>
          ) : chartData ? (
            <Grid container spacing={2}>
              {/* Left: Summary + Timeline */}
              <Grid item xs={12} sm={7}>
                {/* Summary chips row */}
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                >
                  <Chip
                    label="Summary"
                    size="small"
                    sx={{
                      backgroundColor: ORANGE,
                      color: "#fff",
                      fontWeight: 700,
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    All-time totals
                  </Typography>
                </Box>

                <Grid container spacing={1} sx={{ mb: 2 }}>
                  <Grid item xs={4}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        border: "1px solid #e0e0e0",
                        textAlign: "center",
                      }}
                    >
                      <AllInclusiveIcon sx={{ color: ORANGE, fontSize: 16 }} />
                      <Typography
                        variant="h5"
                        fontWeight={700}
                        color="text.primary"
                      >
                        {summary.total.toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        border: `1px solid ${ORANGE}`,
                        textAlign: "center",
                        backgroundColor: "#fff8f3",
                      }}
                    >
                      <StorefrontIcon sx={{ color: ORANGE, fontSize: 16 }} />
                      <Typography
                        variant="h5"
                        fontWeight={700}
                        sx={{ color: ORANGE }}
                      >
                        {summary.deals_page.toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Deals Page
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        border: `1px solid ${BLUE}`,
                        textAlign: "center",
                        backgroundColor: "#f3f9ff",
                      }}
                    >
                      <AutoAwesomeIcon sx={{ color: BLUE, fontSize: 16 }} />
                      <Typography
                        variant="h5"
                        fontWeight={700}
                        sx={{ color: BLUE }}
                      >
                        {summary.smart_flyer.toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Smart Flyer
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {/* Timeline Table */}
                {timeline && timeline.length > 0 && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Chip
                        label="Timeline"
                        size="small"
                        sx={{
                          backgroundColor: "#555",
                          color: "#fff",
                          fontWeight: 700,
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Breakdown by period
                      </Typography>
                    </Box>
                    <TableContainer
                      component={Paper}
                      elevation={0}
                      sx={{
                        border: "1px solid #eee",
                        borderRadius: 2,
                        maxHeight: 260,
                        overflowY: "auto",
                      }}
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                py: 0.75,
                                position: "sticky",
                                top: 0,
                                backgroundColor: "#f5f5f5",
                                zIndex: 1,
                              }}
                            >
                              Period
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: 700,
                                color: ORANGE,
                                py: 0.75,
                                position: "sticky",
                                top: 0,
                                backgroundColor: "#f5f5f5",
                                zIndex: 1,
                              }}
                            >
                              Deals Page
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: 700,
                                color: BLUE,
                                py: 0.75,
                                position: "sticky",
                                top: 0,
                                backgroundColor: "#f5f5f5",
                                zIndex: 1,
                              }}
                            >
                              Smart Flyer
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: 700,
                                py: 0.75,
                                position: "sticky",
                                top: 0,
                                backgroundColor: "#f5f5f5",
                                zIndex: 1,
                              }}
                            >
                              Total
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {timeline.map((entry: any, i: number) => (
                            <TableRow
                              key={i}
                              sx={{
                                "&:last-child td": { border: 0 },
                                "&:hover": { backgroundColor: "#fafafa" },
                              }}
                            >
                              <TableCell sx={{ py: 0.75 }}>
                                {entry.period}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  color: ORANGE,
                                  fontWeight: 600,
                                  py: 0.75,
                                }}
                              >
                                {entry.deals_page.toLocaleString()}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ color: BLUE, fontWeight: 600, py: 0.75 }}
                              >
                                {entry.smart_flyer.toLocaleString()}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ fontWeight: 600, py: 0.75 }}
                              >
                                {entry.total.toLocaleString()}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </Grid>

              {/* Right: Pie Chart */}
              <Grid item xs={12} sm={5}>
                {pieData && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: "1px solid #eee",
                      borderRadius: 2,
                      textAlign: "center",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                      sx={{
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        mb: 1,
                        display: "block",
                      }}
                    >
                      Distribution
                    </Typography>
                    <Box sx={{ maxWidth: 200, mx: "auto" }}>
                      <Pie
                        data={pieData}
                        options={{
                          plugins: {
                            legend: {
                              position: "bottom" as const,
                              labels: { padding: 12, font: { size: 12 } },
                            },
                            tooltip: {
                              callbacks: {
                                label: (ctx) =>
                                  ` ${ctx.label}: ${ctx.parsed.toLocaleString()}`,
                              },
                            },
                          },
                        }}
                      />
                    </Box>
                  </Paper>
                )}
              </Grid>
            </Grid>
          ) : (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography color="text.secondary">No data available</Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #eee" }}>
          <Button
            onClick={onClose}
            variant="contained"
            sx={{
              backgroundColor: ORANGE,
              "&:hover": { backgroundColor: "#d97320" },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};
